/**
 * geminiCache.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Aggressive caching + batch-request grouping layer for Gemini API calls.
 *
 * Goals:
 *  1. Avoid duplicate Gemini calls for the same logical key within a TTL window.
 *  2. Coalesce concurrent callers that ask for the same key into a single
 *     in-flight request (request deduplication).
 *  3. Provide a typed batch helper that groups multiple independent prompts
 *     into a single sequential queue with shared rate-limiting, so callers
 *     don't fire N simultaneous requests.
 *
 * Storage strategy:
 *  - sessionStorage  → survives page navigation within the same tab (default)
 *  - In-memory Map   → fallback when sessionStorage is unavailable (SSR / private)
 *
 * Usage:
 *   import { geminiCache, batchGeminiRequests } from '@/lib/ai/geminiCache';
 *
 *   // Single cached call
 *   const result = await geminiCache.get('my-key', () => callGemini(...), { ttl: 2 * 3600 * 1000 });
 *
 *   // Batch multiple calls with concurrency control
 *   const results = await batchGeminiRequests(items, async (item) => callGemini(item), { concurrency: 3 });
 */

import { getChatCompletion } from './chatCompletion';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CacheOptions {
  /** Time-to-live in milliseconds. Default: 2 hours */
  ttl?: number;
  /** Storage key prefix. Default: 'gc_' */
  prefix?: string;
}

export interface BatchOptions {
  /** Max concurrent Gemini requests. Default: 3 */
  concurrency?: number;
}

interface CacheEntry<T> {
  data: T;
  ts: number;
}

// ─── In-memory fallback ───────────────────────────────────────────────────────
const memoryStore = new Map<string, string>();

function storageGet(key: string): string | null {
  try {
    if (typeof sessionStorage !== 'undefined') return sessionStorage.getItem(key);
  } catch { /* ignore */ }
  return memoryStore.get(key) ?? null;
}

function storageSet(key: string, value: string): void {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(key, value);
      return;
    }
  } catch { /* ignore */ }
  memoryStore.set(key, value);
}

// ─── In-flight deduplication registry ────────────────────────────────────────
// Maps storageKey → Promise so concurrent callers share one request.
const inFlight = new Map<string, Promise<any>>();

// ─── Core cache helper ────────────────────────────────────────────────────────

const DEFAULT_TTL = 2 * 60 * 60 * 1000; // 2 hours
const DEFAULT_PREFIX = 'gc_';

/**
 * Retrieve a value from cache or compute it via `fetcher`.
 * Concurrent calls with the same key share a single in-flight request.
 */
async function get<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: CacheOptions = {}
): Promise<T> {
  const ttl = options.ttl ?? DEFAULT_TTL;
  const storageKey = (options.prefix ?? DEFAULT_PREFIX) + key;

  // 1. Check persistent cache
  try {
    const raw = storageGet(storageKey);
    if (raw) {
      const entry: CacheEntry<T> = JSON.parse(raw);
      if (Date.now() - entry.ts < ttl) {
        return entry.data;
      }
    }
  } catch { /* ignore corrupt entries */ }

  // 2. Deduplicate in-flight requests
  if (inFlight.has(storageKey)) {
    return inFlight.get(storageKey) as Promise<T>;
  }

  // 3. Fetch and cache
  const promise = fetcher().then((data) => {
    try {
      storageSet(storageKey, JSON.stringify({ data, ts: Date.now() } satisfies CacheEntry<T>));
    } catch { /* ignore storage errors */ }
    inFlight.delete(storageKey);
    return data;
  }).catch((err) => {
    inFlight.delete(storageKey);
    throw err;
  });

  inFlight.set(storageKey, promise);
  return promise;
}

/**
 * Explicitly invalidate a cache entry.
 */
function invalidate(key: string, prefix = DEFAULT_PREFIX): void {
  const storageKey = prefix + key;
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(storageKey);
  } catch { /* ignore */ }
  memoryStore.delete(storageKey);
}

export const geminiCache = { get, invalidate };

// ─── Batch request helper ─────────────────────────────────────────────────────

/**
 * Process an array of items through `processor` with bounded concurrency.
 * Returns results in the same order as `items`.
 * Failed items resolve to `null` (never throws).
 */
export async function batchGeminiRequests<TItem, TResult>(
  items: TItem[],
  processor: (item: TItem, index: number) => Promise<TResult>,
  options: BatchOptions = {}
): Promise<Array<TResult | null>> {
  const concurrency = options.concurrency ?? 3;
  const results: Array<TResult | null> = new Array(items.length).fill(null);

  // Process in chunks of `concurrency`
  for (let i = 0; i < items.length; i += concurrency) {
    const chunk = items.slice(i, i + concurrency);
    const settled = await Promise.allSettled(
      chunk.map((item, j) => processor(item, i + j))
    );
    settled.forEach((outcome, j) => {
      results[i + j] = outcome.status === 'fulfilled' ? outcome.value : null;
    });
  }

  return results;
}

// ─── Pre-built Gemini helpers used across the dashboard ──────────────────────

const GEMINI_MODEL = 'gemini/gemini-2.5-flash';

/**
 * Score a single city pin 1-100 for roofing expansion potential.
 * Results are cached for 2 hours.
 */
export async function fetchAiScore(
  pinId: string,
  cityName: string,
  state: string,
  cityStatus: string
): Promise<number> {
  return geminiCache.get<number>(
    `ai_score_${pinId}`,
    async () => {
      const res = await getChatCompletion(
        'GEMINI',
        GEMINI_MODEL,
        [
          {
            role: 'system',
            content:
              'You are a roofing market scoring AI. Score a city 1-100 for roofing expansion potential based on: population size/growth, storm/hail history, competition level, and home age. Respond with ONLY a single integer number between 1 and 100. No explanation, no text, just the number.',
          },
          {
            role: 'user',
            content: `Score this city for roofing expansion:\nCity: ${cityName}, ${state}\nStatus: ${cityStatus}\nConsider: population (higher = better), storm history (more hail = better), competition (less = better), home age (older = more replacements needed).\nRespond with only a number 1-100.`,
          },
        ],
        { max_tokens: 10, temperature: 0.3 }
      );
      const raw = res?.choices?.[0]?.message?.content?.trim() ?? '50';
      return Math.min(100, Math.max(1, parseInt(raw.replace(/\D/g, ''), 10) || 50));
    },
    { ttl: 2 * 60 * 60 * 1000 }
  );
}

/**
 * Fetch AI scores for multiple pins using bounded concurrency + per-pin caching.
 */
export async function fetchAiScoresBatch(
  pins: Array<{ id: string; city_name: string; state: string; city_status: string }>,
  concurrency = 3
): Promise<Record<string, number>> {
  const results = await batchGeminiRequests(
    pins,
    (pin) => fetchAiScore(pin.id, pin.city_name, pin.state, pin.city_status),
    { concurrency }
  );

  const scores: Record<string, number> = {};
  pins.forEach((pin, i) => {
    if (results[i] != null) scores[pin.id] = results[i] as number;
  });
  return scores;
}

/**
 * Fetch full market data (score + stats + brief) for a searched city.
 * Cached for 2 hours keyed by city+state.
 */
export interface MarketSearchData {
  aiScore: number;
  population: string;
  medianHomeValue: string;
  stormHistory: string;
  competitionDensity: string;
  marketBrief: string;
}

export async function fetchMarketSearchData(
  city: string,
  state: string
): Promise<MarketSearchData> {
  const cacheKey = `market_search_${city.toLowerCase().replace(/\s+/g, '_')}_${state.toLowerCase()}`;
  return geminiCache.get<MarketSearchData>(
    cacheKey,
    async () => {
      const res = await getChatCompletion(
        'GEMINI',
        GEMINI_MODEL,
        [
          {
            role: 'system',
            content: `You are a roofing market research analyst. For the given city, return a JSON object with these exact keys:
{
  "aiScore": number (1-100, roofing expansion potential),
  "population": string (e.g. "312,000"),
  "medianHomeValue": string (e.g. "$245,000"),
  "stormHistory": string (e.g. "High — 8 hail events/5yr"),
  "competitionDensity": string (e.g. "Moderate — 12 local roofers"),
  "marketBrief": string (3-4 sentences covering opportunity, risks, and recommended next action for a roofing company)
}
Return ONLY valid JSON, no markdown, no explanation.`,
          },
          {
            role: 'user',
            content: `Analyze this city for roofing market expansion: ${city}${state ? `, ${state}` : ''}`,
          },
        ],
        { max_tokens: 400, temperature: 0.4 }
      );
      const raw = res?.choices?.[0]?.message?.content ?? '{}';
      const cleaned = raw.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsed = JSON.parse(cleaned);
      return {
        aiScore: Math.min(100, Math.max(1, Number(parsed.aiScore) || 50)),
        population: parsed.population ?? '—',
        medianHomeValue: parsed.medianHomeValue ?? '—',
        stormHistory: parsed.stormHistory ?? '—',
        competitionDensity: parsed.competitionDensity ?? '—',
        marketBrief: parsed.marketBrief ?? '',
      };
    },
    { ttl: 2 * 60 * 60 * 1000 }
  );
}

/**
 * Fetch a side-by-side comparison of two searched markets.
 * Cached for 2 hours keyed by both city+state pairs (order-independent).
 */
export async function fetchMarketComparison(
  marketA: { city: string; state: string; aiScore: number; population: string; stormHistory: string; competitionDensity: string },
  marketB: { city: string; state: string; aiScore: number; population: string; stormHistory: string; competitionDensity: string }
): Promise<string> {
  // Sort keys so A/B order doesn't create duplicate cache entries
  const keyParts = [
    `${marketA.city}_${marketA.state}`,
    `${marketB.city}_${marketB.state}`,
  ].sort();
  const cacheKey = `market_compare_${keyParts[0]}_vs_${keyParts[1]}`.toLowerCase().replace(/\s+/g, '_');

  return geminiCache.get<string>(
    cacheKey,
    async () => {
      const res = await getChatCompletion(
        'GEMINI',
        GEMINI_MODEL,
        [
          {
            role: 'system',
            content:
              'You are a roofing market expansion strategist for NextRoof 360. Compare two markets and give a clear recommendation. Format: "[City A, State] vs. [City B, State] — [one sentence recommendation of which to enter first and why]." Keep under 30 words.',
          },
          {
            role: 'user',
            content: `Compare these two markets for roofing expansion:

Market A: ${marketA.city}, ${marketA.state}
AI Score: ${marketA.aiScore}/100
Population: ${marketA.population}
Storm History: ${marketA.stormHistory}
Competition: ${marketA.competitionDensity}

Market B: ${marketB.city}, ${marketB.state}
AI Score: ${marketB.aiScore}/100
Population: ${marketB.population}
Storm History: ${marketB.stormHistory}
Competition: ${marketB.competitionDensity}

Which market should NextRoof 360 enter first?`,
          },
        ],
        { max_tokens: 80, temperature: 0.4 }
      );
      return res?.choices?.[0]?.message?.content ?? '';
    },
    { ttl: 2 * 60 * 60 * 1000 }
  );
}

/**
 * Fetch a Gemini city brief for a map pin.
 * Cached for 1 hour.
 */
export async function fetchCityBrief(
  pinId: string,
  cityName: string,
  state: string,
  cityStatus: string,
  radiusRings: number[]
): Promise<string> {
  return geminiCache.get<string>(
    `city_brief_${pinId}`,
    async () => {
      const res = await getChatCompletion(
        'GEMINI',
        GEMINI_MODEL,
        [
          {
            role: 'system',
            content:
              'You are a roofing market expansion analyst for NextRoof 360. Generate a concise market brief (3-4 sentences) for a city pin on the expansion map. Cover: market opportunity, key risks, and a recommended next action. Be specific and actionable.',
          },
          {
            role: 'user',
            content: `Generate a market brief for: ${cityName}, ${state}\nStatus: ${cityStatus}\nTerritory rings: ${radiusRings.length > 0 ? radiusRings.map((r) => `${r}mi`).join(', ') : 'None set'}`,
          },
        ],
        { max_tokens: 200, temperature: 0.7 }
      );
      return res?.choices?.[0]?.message?.content ?? '';
    },
    { ttl: 60 * 60 * 1000 }
  );
}

/**
 * Fetch Market Fundamentals data for a city pin.
 * Cached for 2 hours.
 */
export interface MarketFundamentalsData {
  totalAddressableMarket: string;
  medianHomeAge: string;
  homeownershipRate: string;
  avgHomeValueRange: string;
  populationGrowthTrend: string;
}

export async function fetchMarketFundamentals(
  pinId: string,
  cityName: string,
  state: string
): Promise<MarketFundamentalsData> {
  return geminiCache.get<MarketFundamentalsData>(
    `market_fundamentals_${pinId}`,
    async () => {
      const res = await getChatCompletion(
        'GEMINI',
        GEMINI_MODEL,
        [
          {
            role: 'system',
            content: `You are a roofing market research analyst. For the given city, return a JSON object with these exact keys:
{
  "totalAddressableMarket": string (e.g. "$42M/yr — est. 8,400 roofs"),
  "medianHomeAge": string (e.g. "34 years"),
  "homeownershipRate": string (e.g. "67%"),
  "avgHomeValueRange": string (e.g. "$210K – $380K"),
  "populationGrowthTrend": string (e.g. "+2.1%/yr — Growing")
}
Return ONLY valid JSON, no markdown, no explanation.`,
          },
          {
            role: 'user',
            content: `Provide market fundamentals for roofing expansion in: ${cityName}, ${state}`,
          },
        ],
        { max_tokens: 300, temperature: 0.3 }
      );
      const raw = res?.choices?.[0]?.message?.content ?? '{}';
      const cleaned = raw.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsed = JSON.parse(cleaned);
      return {
        totalAddressableMarket: parsed.totalAddressableMarket ?? '—',
        medianHomeAge: parsed.medianHomeAge ?? '—',
        homeownershipRate: parsed.homeownershipRate ?? '—',
        avgHomeValueRange: parsed.avgHomeValueRange ?? '—',
        populationGrowthTrend: parsed.populationGrowthTrend ?? '—',
      };
    },
    { ttl: 2 * 60 * 60 * 1000 }
  );
}

/**
 * Fetch Storm Intelligence data for a city pin.
 * Cached for 2 hours.
 */
export interface StormIntelligenceData {
  hailEventFrequency: string;
  lastMajorStormEvent: string;
  stormSeasonPeakMonths: string;
  insuranceClaimDensity: string;
}

export async function fetchStormIntelligence(
  pinId: string,
  cityName: string,
  state: string
): Promise<StormIntelligenceData> {
  return geminiCache.get<StormIntelligenceData>(
    `storm_intelligence_${pinId}`,
    async () => {
      const res = await getChatCompletion(
        'GEMINI',
        GEMINI_MODEL,
        [
          {
            role: 'system',
            content: `You are a storm and weather intelligence analyst for roofing markets. For the given city, return a JSON object with these exact keys:
{
  "hailEventFrequency": string (e.g. "6.2 events/yr — High"),
  "lastMajorStormEvent": string (e.g. "Apr 2024 — Cat 3 Hail"),
  "stormSeasonPeakMonths": string (e.g. "Apr – Jun, Sep – Oct"),
  "insuranceClaimDensity": string (e.g. "High — 18 claims/1,000 homes")
}
Return ONLY valid JSON, no markdown, no explanation.`,
          },
          {
            role: 'user',
            content: `Provide storm intelligence data for roofing market in: ${cityName}, ${state}`,
          },
        ],
        { max_tokens: 250, temperature: 0.3 }
      );
      const raw = res?.choices?.[0]?.message?.content ?? '{}';
      const cleaned = raw.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsed = JSON.parse(cleaned);
      return {
        hailEventFrequency: parsed.hailEventFrequency ?? '—',
        lastMajorStormEvent: parsed.lastMajorStormEvent ?? '—',
        stormSeasonPeakMonths: parsed.stormSeasonPeakMonths ?? '—',
        insuranceClaimDensity: parsed.insuranceClaimDensity ?? '—',
      };
    },
    { ttl: 2 * 60 * 60 * 1000 }
  );
}

/**
 * Fetch Competitive Landscape data for a city pin.
 * Cached for 2 hours.
 */
export interface CompetitiveLandscapeData {
  estimatedContractors: string;
  marketSaturationScore: string;
  dominantCompetitors: string;
  avgCompetitorPricing: string;
}

export async function fetchCompetitiveLandscape(
  pinId: string,
  cityName: string,
  state: string
): Promise<CompetitiveLandscapeData> {
  return geminiCache.get<CompetitiveLandscapeData>(
    `competitive_landscape_${pinId}`,
    async () => {
      const res = await getChatCompletion(
        'GEMINI',
        GEMINI_MODEL,
        [
          {
            role: 'system',
            content: `You are a competitive intelligence analyst for roofing markets. For the given city, return a JSON object with these exact keys:
{
  "estimatedContractors": string (e.g. "~85 licensed roofing contractors"),
  "marketSaturationScore": string (e.g. "Medium — moderate competition"),
  "dominantCompetitors": string (e.g. "ABC Roofing, SkyTop Contractors, Premier Roof Co."),
  "avgCompetitorPricing": string (e.g. "Mid-range — $8,500–$14,000 avg job")
}
Return ONLY valid JSON, no markdown, no explanation.`,
          },
          {
            role: 'user',
            content: `Provide competitive landscape data for roofing market in: ${cityName}, ${state}`,
          },
        ],
        { max_tokens: 300, temperature: 0.3 }
      );
      const raw = res?.choices?.[0]?.message?.content ?? '{}';
      const cleaned = raw.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsed = JSON.parse(cleaned);
      return {
        estimatedContractors: parsed.estimatedContractors ?? '—',
        marketSaturationScore: parsed.marketSaturationScore ?? '—',
        dominantCompetitors: parsed.dominantCompetitors ?? '—',
        avgCompetitorPricing: parsed.avgCompetitorPricing ?? '—',
      };
    },
    { ttl: 2 * 60 * 60 * 1000 }
  );
}

/**
 * Fetch NextRoof 360-Specific Intelligence data for a city pin.
 * Cached for 2 hours.
 */
export interface OpenbooqIntelligenceData {
  estimatedRampTime: string;
  recommendedCrewSize: string;
  suggestedLeadSourceMix: string;
  riskFlags: string;
}

export async function fetchOpenbooqIntelligence(
  pinId: string,
  cityName: string,
  state: string
): Promise<OpenbooqIntelligenceData> {
  return geminiCache.get<OpenbooqIntelligenceData>(
    `openbooq_intelligence_${pinId}`,
    async () => {
      const res = await getChatCompletion(
        'GEMINI',
        GEMINI_MODEL,
        [
          {
            role: 'system',
            content: `You are an NextRoof 360 market launch strategist. NextRoof 360 is a roofing company expanding into new markets. For the given city, return a JSON object with these exact keys:
{
  "estimatedRampTime": string (e.g. "6–8 weeks to first job — similar to Austin TX launch"),
  "recommendedCrewSize": string (e.g. "2 crews to launch, scale to 4 by month 3"),
  "suggestedLeadSourceMix": string (e.g. "Google Ads heavy (60%), door-to-door secondary (25%), referral (15%)"),
  "riskFlags": string (e.g. "High HOA density, complex permit process (avg 3 weeks), no union requirements")
}
Return ONLY valid JSON, no markdown, no explanation.`,
          },
          {
            role: 'user',
            content: `Provide NextRoof 360 launch intelligence for roofing expansion in: ${cityName}, ${state}`,
          },
        ],
        { max_tokens: 350, temperature: 0.4 }
      );
      const raw = res?.choices?.[0]?.message?.content ?? '{}';
      const cleaned = raw.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsed = JSON.parse(cleaned);
      return {
        estimatedRampTime: parsed.estimatedRampTime ?? '—',
        recommendedCrewSize: parsed.recommendedCrewSize ?? '—',
        suggestedLeadSourceMix: parsed.suggestedLeadSourceMix ?? '—',
        riskFlags: parsed.riskFlags ?? '—',
      };
    },
    { ttl: 2 * 60 * 60 * 1000 }
  );
}

/**
 * Fetch Launch Planning data for a city pin.
 * Cached for 2 hours.
 */
export interface LaunchPlanningData {
  recommendedLaunchWindow: string;
  goNoGoScore: string;
  confidenceLevel: string;
  nextActions: [string, string, string];
}

export async function fetchLaunchPlanning(
  pinId: string,
  cityName: string,
  state: string
): Promise<LaunchPlanningData> {
  return geminiCache.get<LaunchPlanningData>(
    `launch_planning_${pinId}`,
    async () => {
      const res = await getChatCompletion(
        'GEMINI',
        GEMINI_MODEL,
        [
          {
            role: 'system',
            content: `You are an NextRoof 360 market launch strategist. NextRoof 360 is a roofing company expanding into new markets. For the given city, return a JSON object with these exact keys:
{
  "recommendedLaunchWindow": string (e.g. "Q2 2026 — Post-storm season, low competition window"),
  "goNoGoScore": string (e.g. "GO" or "NO-GO" or "CONDITIONAL GO"),
  "confidenceLevel": string (e.g. "82% confidence"),
  "nextActions": [string, string, string] (exactly 3 specific action items for this market)
}
Return ONLY valid JSON, no markdown, no explanation.`,
          },
          {
            role: 'user',
            content: `Provide launch planning data for NextRoof 360 roofing expansion in: ${cityName}, ${state}`,
          },
        ],
        { max_tokens: 400, temperature: 0.4 }
      );
      const raw = res?.choices?.[0]?.message?.content ?? '{}';
      const cleaned = raw.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsed = JSON.parse(cleaned);
      return {
        recommendedLaunchWindow: parsed.recommendedLaunchWindow ?? '—',
        goNoGoScore: parsed.goNoGoScore ?? '—',
        confidenceLevel: parsed.confidenceLevel ?? '—',
        nextActions: Array.isArray(parsed.nextActions) && parsed.nextActions.length >= 3
          ? [parsed.nextActions[0], parsed.nextActions[1], parsed.nextActions[2]]
          : ['—', '—', '—'],
      };
    },
    { ttl: 2 * 60 * 60 * 1000 }
  );
}
